import React, { useEffect, useState } from "react";
import { Button, Header, Icon, Table, Pagination, Segment, Label, Grid, Input, Modal } from "semantic-ui-react";
import { toast } from "react-toastify";
import { ToasterConfig } from "../../../config/ToasterConfig";
import TooltipInfo from "../../tooltip/tooltip";
import { SessionUser } from "../../../models/session-user";
import SessionStorage from "../../../store/session";
import { useNavigate } from "react-router-dom";
import ProductForm from "./create-product-form"; // Você precisará criar esse formulário de criação/edição de produto
import {ProductDto, ProductFilter} from "../../../models/requests/product-filter";
import useProductService from "../../../services/backoffice/hooks/useProductsService"; // Altere conforme necessário
import { getProductTypeName } from "../../../utils/enum-helpers";

interface ProductsListProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: () => void;
}

const ProductsList: React.FC<ProductsListProps> = ({ isModal = false, open = false, onClose }) => {
    const [products, setProducts] = useState<ProductDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<ProductFilter>({ Page: 1 });
    const [totalPages, setTotalPages] = useState<number>(0);
    const { getProducts, deleteProduct, activateProduct, deactivateProduct } = useProductService();
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const navigate = useNavigate();
    const [showFilters, setShowFilters] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [editProductId, setEditProductId] = useState<string | null>(null);

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user);
        }
        updateSessionUser();
    }, []);

    useEffect(() => {
        async function loadProducts() {
            setLoading(true);
            try {
                const response = await getProducts(filter);
                setProducts(response.itens);
                setTotalPages(response.totalPages);
            } catch (err: any) {
                setError("Erro ao carregar produtos.");
            } finally {
                setLoading(false);
            }
        }
        loadProducts();
    }, [filter]);

    const handleDelete = async (id: string) => {
        try {
            const result = window.confirm("Tem certeza que deseja excluir este produto?");
            if (result) {
                await deleteProduct(id);
                toast.success("Produto excluído com sucesso", ToasterConfig.success);
                setProducts(products.filter((p) => p.id !== id));
            }
        } catch (err: any) {
            toast.error("Erro ao excluir produto", ToasterConfig.error);
        }
    };

    const handleActivation = async (product: ProductDto) => {
        try {
            const result = window.confirm(`Tem certeza que deseja ${product.isActive ? 'desativar' : 'ativar'} este produto?`);
            if (result) {
                if (product.isActive) {
                    await deactivateProduct(product.id);
                } else {
                    await activateProduct(product.id);
                }
                setFilter({ ...filter }); // Atualiza a lista
                toast.success(`Produto ${product.isActive ? 'desativado' : 'ativado'} com sucesso.`, ToasterConfig.success);
            }
        } catch (err: any) {
            toast.error("Erro ao alterar status do produto", ToasterConfig.error);
        }
    };

    const handleEdit = (id: string) => {
        setEditProductId(id);
        setModalOpen(true);
    };

    const formContent = (
        <Segment raised style={{ maxWidth: "900px", margin: "5px auto", padding: "20px" }}>
            <Header as="h3" textAlign="center" color={'green'}>
                Produtos
            </Header>
            <Table.Row>
                <Table.HeaderCell>
                    {sessionUser?.userData?.profile === 'Admin' && (
                        <Button color="green" onClick={() => setModalOpen(true)}>
                            <Icon name='plus' /> Novo
                        </Button>
                    )}
                </Table.HeaderCell>
                <Table.HeaderCell colSpan="4">
                    <Button onClick={toggleFilters} color="green">
                        <Icon name={showFilters ? 'hide' : 'unhide'} />
                        {showFilters ? 'Ocultar filtros' : 'Exibir filtros'}
                    </Button>
                </Table.HeaderCell>
            </Table.Row>

            {showFilters && (
                <Segment>
                    <Grid stackable doubling padded>
                        <Grid.Row>
                            <Grid.Column width={6}>
                                <label htmlFor="code">Código</label>
                                <Input
                                    fluid
                                    id="code"
                                    icon="search"
                                    type="text"
                                    placeholder="Código do produto"
                                    value={filter?.code ?? ''}
                                    onChange={(event) => setFilter({ ...filter, code: event.target.value })}
                                />
                            </Grid.Column>

                            <Grid.Column width={6}>
                                <label htmlFor="name">Nome</label>
                                <Input
                                    fluid
                                    id="name"
                                    icon="search"
                                    type="text"
                                    placeholder="Nome do produto"
                                    value={filter?.name ?? ''}
                                    onChange={(event) => setFilter({ ...filter, name: event.target.value })}
                                />
                            </Grid.Column>

                            <Grid.Column width={2} verticalAlign="bottom">
                                <Button color="green" onClick={() => setFilter({ ...filter, Page: 1 })}>Buscar</Button>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
            )}

            <Table color="green">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>#</Table.HeaderCell>
                        <Table.HeaderCell>Código</Table.HeaderCell>
                        <Table.HeaderCell>Nome</Table.HeaderCell>
                        <Table.HeaderCell>Tipo</Table.HeaderCell>
                        <Table.HeaderCell>Descrição</Table.HeaderCell>
                        <Table.HeaderCell>Ativo</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {products?.map((product) => (
                        <Table.Row key={product.id}>
                            <Table.Cell>
                                <Button.Group>
                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Editar produto"
                                            trigger={
                                                <Button
                                                    color="green"
                                                    onClick={() => handleEdit(product.id)}
                                                    icon="edit"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Excluir produto"
                                            trigger={
                                                <Button
                                                    color="red"
                                                    onClick={() => handleDelete(product.id)}
                                                    icon="trash"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content={product.isActive ? "Desativar produto" : "Ativar produto"}
                                            trigger={
                                                <Button
                                                    color={product.isActive ? 'red' : 'green'}
                                                    onClick={() => handleActivation(product)}
                                                    icon={product.isActive ? 'power off' : 'power'}
                                                />
                                            }
                                        />
                                    )}
                                </Button.Group>
                            </Table.Cell>
                            <Table.Cell>{product.code}</Table.Cell>
                            <Table.Cell>{product.name}</Table.Cell>
                            <Table.Cell>{getProductTypeName(product.type)}</Table.Cell>
                            <Table.Cell>{product.description}</Table.Cell>
                            <Table.Cell>
                                <Label color={product.isActive ? 'green' : 'red'}>
                                    {product.isActive ? 'Sim' : 'Não'}
                                </Label>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>

            <Pagination
                totalPages={totalPages}
                defaultActivePage={filter?.Page ?? 1}
                onPageChange={(e, { activePage }) => setFilter({ ...filter, Page: activePage as number })}
            />
        </Segment>
    );

    return (
        <>
            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                size="tiny"
                style={{ width: '880px', maxWidth: '95vw', padding: '20px' }}
            >
                <ProductForm
                    isModal
                    open={modalOpen}
                    productId={editProductId || undefined}
                    onClose={() => {
                        setModalOpen(false);
                        setEditProductId(null);
                        setFilter({ ...filter });
                    }}
                />
            </Modal>

            {formContent}
        </>
    );
};

export default ProductsList;
